import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://mylocalpharmacy.uk";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = "Wilberforce Pharmacy";

interface SEOOptions {
  title: string;
  description: string;
  path?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  articlePublishedTime?: string;
  noindex?: boolean;
}

function setMetaTag(property: string, content: string, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let tag = document.querySelector<HTMLMetaElement>(`meta[${attr}="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

export function useSEO({
  title,
  description,
  path,
  ogImage,
  ogType = "website",
  articlePublishedTime,
  noindex = false,
}: SEOOptions) {
  const location = useLocation();
  const canonicalPath = path || location.pathname;
  const canonicalUrl = `${BASE_URL}${canonicalPath === "/" ? "" : canonicalPath}`;
  const imageUrl = ogImage || DEFAULT_OG_IMAGE;

  useEffect(() => {
    // Title
    document.title = title;

    // Meta description
    setMetaTag("description", description);

    // Canonical
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);

    // Open Graph
    setMetaTag("og:title", title, true);
    setMetaTag("og:description", description, true);
    setMetaTag("og:url", canonicalUrl, true);
    setMetaTag("og:type", ogType, true);
    setMetaTag("og:image", imageUrl, true);
    setMetaTag("og:site_name", SITE_NAME, true);

    // Twitter Card
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);
    setMetaTag("twitter:image", imageUrl);

    // Article-specific
    if (ogType === "article" && articlePublishedTime) {
      setMetaTag("article:published_time", articlePublishedTime, true);
    }

    // Robots noindex
    setMetaTag("robots", noindex ? "noindex, nofollow" : "index, follow");

    return () => {
      // Reset to defaults on unmount
      if (link) link.setAttribute("href", BASE_URL);
    };
  }, [title, description, canonicalUrl, imageUrl, ogType, articlePublishedTime, noindex]);
}
